import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { useTranslation } from "react-i18next";
import { graphqlClient } from "@/lib/graphql";
import type { Collection } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { Modal } from "@/components/ui/Modal";
import { usePageTitle } from "@/hooks/usePageTitle";

const MY_COLLECTIONS_QUERY = gql`
  query MyCollections {
    myCollections {
      id
      name
      coverImageUrl
      listingsCount
      createdAt
    }
  }
`;

const CREATE_COLLECTION_MUTATION = gql`
  mutation CreateCollection($name: String!) {
    createCollection(name: $name) {
      collection {
        id
        name
      }
      errors
    }
  }
`;

const CANCEL_COLLECTION_MUTATION = gql`
  mutation CancelCollection($id: ID!) {
    cancelCollection(id: $id) {
      success
      errors
    }
  }
`;

export function MyCollectionsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [cancelId, setCancelId] = useState<string | null>(null);

  usePageTitle(t("collections.my_collections"));

  const { data, isLoading } = useQuery({
    queryKey: ["myCollections"],
    queryFn: () =>
      graphqlClient.request<{ myCollections: Collection[] }>(MY_COLLECTIONS_QUERY),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      graphqlClient.request<{ createCollection: { collection: Collection | null; errors: string[] } }>(
        CREATE_COLLECTION_MUTATION,
        { name },
      ),
    onSuccess: (data) => {
      if (data.createCollection.collection) {
        queryClient.invalidateQueries({ queryKey: ["myCollections"] });
        setShowCreate(false);
        setNewName("");
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) =>
      graphqlClient.request<{ cancelCollection: { success: boolean; errors: string[] } }>(
        CANCEL_COLLECTION_MUTATION,
        { id },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myCollections"] });
      setCancelId(null);
    },
  });

  const collections = data?.myCollections || [];

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t("collections.my_collections")}</h1>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          {t("collections.create")}
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : collections.length === 0 ? (
        <EmptyState
          title={t("collections.no_collections")}
          action={
            <button onClick={() => setShowCreate(true)} className="btn-primary">
              {t("collections.create")}
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {collections.map((collection) => (
            <div key={collection.id} className="card overflow-hidden transition-shadow hover:shadow-lg">
              <Link to={`/collections/${collection.id}`}>
                <div className="aspect-video overflow-hidden bg-gray-100">
                  {collection.coverImageUrl ? (
                    <img
                      src={collection.coverImageUrl}
                      alt={collection.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-400">
                      <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="truncate font-semibold text-gray-900">{collection.name}</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {t("collections.count", { count: collection.listingsCount })}
                  </p>
                </div>
              </Link>
              <div className="border-t border-gray-100 px-3 py-2">
                <button
                  onClick={() => setCancelId(collection.id)}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  {t("collections.cancel_collection")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setNewName("");
        }}
        title={t("collections.create_title")}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("collections.name_label")}
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t("collections.name_placeholder")}
              className="input mt-1 w-full"
              autoFocus
            />
          </div>
          {createMutation.data?.createCollection?.errors?.length ? (
            <p className="text-sm text-red-600">
              {createMutation.data.createCollection.errors.join(", ")}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowCreate(false);
                setNewName("");
              }}
              className="btn-secondary"
            >
              {t("collections.cancel")}
            </button>
            <button
              onClick={() => newName.trim() && createMutation.mutate(newName.trim())}
              disabled={!newName.trim() || createMutation.isPending}
              className="btn-primary"
            >
              {createMutation.isPending ? t("collections.saving") : t("collections.save")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Cancel confirmation */}
      <Modal
        open={cancelId !== null}
        onClose={() => setCancelId(null)}
        title={t("collections.cancel_collection")}
      >
        <p className="text-sm text-gray-600">{t("collections.confirm_cancel")}</p>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={() => setCancelId(null)} className="btn-secondary">
            {t("collections.keep")}
          </button>
          <button
            onClick={() => cancelId && cancelMutation.mutate(cancelId)}
            disabled={cancelMutation.isPending}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {cancelMutation.isPending ? "..." : t("collections.cancel_collection")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
